<template>
  <div>
    <BaseCottageListPage
      v-if="!pending && cottagesData?.cottages && cottagesMeta?.pagination"
      :cottages-data="cottagesData"
      :cottages-meta="cottagesMeta"
      :location-name="locationName"
      :facets-data="facetsData"
    >
      <template #default>
        <CottageListReviews :items="reviewsData" :title="locationName" />
        <CottageListFacetsContent :facets-data="facetsData" />
        <CottageListCloseCottages
          :items="closeCottagesData"
          :title="locationName"
        />
      </template>
    </BaseCottageListPage>
  </div>
</template>

<script setup lang="ts">
import { useMenuBreadcrumbStore } from '~~/modules/menu/stores/useMenuBreadcrumbStore'
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'
import { useCottageListPage } from '~~/modules/views/cottage-list/composables/useCottageListPage'
import { useSendCottageStats } from '~~/modules/views/cottage-list/composables/useSendCottageStats'
import { serverFetch as getFacetModules } from '~~/modules/api/rurality/v3/search/seo/facet-modules'
import { serverFetch as getDestinationModules } from '~~/modules/api/rurality/v3/search/seo/destination-modules'
import { serverFetch as fetchCottageReviews } from '~~/modules/api/rurality/v3/cottage-reviews/destination/detail'
import type { schemaType as reviewsSchemaType } from '~~/modules/api/rurality/v3/cottage-reviews/destination/detail/schema'
import type { schemaType as facetModulesSchemaType } from '~~/modules/api/rurality/v3/search/seo/facet-modules/schema'

definePageMeta({
  name: 'casas-rurales-facets',
})

const { setHeadInfo } = useHeadInfoComposable()
const breadCrumbMenuStore = useMenuBreadcrumbStore()

const route = useRoute()

const {
  pending,
  cottagesData,
  cottagesMeta,
  facetsData,
  error,
  getCurrentLocationName,
  initializePage,
  getBreadcrumbData,
  getHeadInfoData,
  showErrorPage,
} = useCottageListPage({})

await initializePage()

if (import.meta.client) {
  const { sendCottageStats, sendCottageStatsCloudwatch } = useSendCottageStats()
  sendCottageStats(cottagesData.value)
  sendCottageStatsCloudwatch(cottagesData.value)
}

let closeCottagesData: facetModulesSchemaType['modules'] = []
let reviewsData: reviewsSchemaType['reviews'] = []
let locationName: string

if (error.value) {
  showErrorPage(error.value)
} else {
  locationName = getCurrentLocationName()
  setHeadInfo(getHeadInfoData(true))
  const breadcrumbData = getBreadcrumbData()
  if (breadcrumbData) {
    breadCrumbMenuStore.setMenuBreadcrumb(
      breadcrumbData.name,
      breadcrumbData.middleItems,
    )
  }

  const page = route.query.page ? Number(route.query.page) : 1
  const routePath = route.path
  const pathSegments = routePath.split('/')
  const facetSlug = pathSegments[1]

  if (facetSlug) {
    const facetCall = await getFacetModules({
      facetSlug,
      searchListType: 'facet',
      page,
    })

    if (facetCall?.success?.data?.modules?.length) {
      closeCottagesData = facetCall.success.data.modules
    } else if (cottagesData.value?.destination) {
      const destinationCall = await getDestinationModules({
        destinationId: cottagesData.value.destination.id,
        searchListType: 'facet',
        page,
      })
      closeCottagesData = destinationCall?.success?.data?.modules || []
    } else {
      closeCottagesData = []
    }
  } else {
    closeCottagesData = []
  }

  if (cottagesData.value?.destination) {
    const reviewsDataCall = await fetchCottageReviews({
      destination_slug: cottagesData.value.destination.slug,
      limit: 6,
    })
    reviewsData = reviewsDataCall.success?.data.reviews || []
  }
}
</script>
